import { useCallback, useEffect, useMemo, useState } from 'react';
import { object, string } from 'yup';
import { Grid } from '@mui/material';
import { makeStyles } from '../../../themes';
import { useFormik } from 'formik';
import { SelectInput, SwitchInput, TextInput } from '../inputs';
import { useDispatch, addResource, getUsersBasicList } from '../../../state';
import { useRefresh, useIsMobile } from '../../../hooks';
import { CheckInSmallParcelReceiptRequest, IdNamePair } from '../../../types';
import { defaultReceiptCheckInSmallParcel } from '../../../pages/receipt/utils';
import { FormModal } from './FormModal';
import { MobileModalForm } from '../../../themes/mobile/MobileModalForm';

interface Props {
  open: boolean;
  handleClose: () => any;
  receiptId?: number | string;
  onSubmit?: (data: CheckInSmallParcelReceiptRequest) => void;
  warehouseId: number | string;
  setViewVersion?: React.Dispatch<React.SetStateAction<number>>;
  checkInPath?: 'small-parcel/check-in' | 'check-in-without-appointment';
  isNonSmallParcel?: boolean;
  isDrayage?: boolean;
}

export function ReceiptCheckInSmallParcelForm({
  open,
  handleClose,
  receiptId,
  warehouseId,
  setViewVersion,
  onSubmit,
  checkInPath = 'small-parcel/check-in',
  isNonSmallParcel = false,
  isDrayage = false,
}: Props) {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const refresh = useRefresh();
  const isMobile = useIsMobile();

  const [submitting, setSubmitting] = useState(false);
  const [managers, setManagers] = useState<Array<IdNamePair>>([]);

  useEffect(() => {
    (async function () {
      const { data } = await dispatch(
        getUsersBasicList({ warehouseID: warehouseId, userTypes: ['Manager'] }),
      );
      if (data) {
        setManagers(data);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warehouseId]);

  const handleSubmit = useCallback(
    async (values: CheckInSmallParcelReceiptRequest) => {
      setSubmitting(true);
      const payload: CheckInSmallParcelReceiptRequest = {
        ...values,
        warehouseStagingLocationName: values.saveInNonSpecificLocation
          ? undefined
          : values.warehouseStagingLocationName,
      };

      if (onSubmit) {
        onSubmit(payload);
        handleClose();
      } else {
        payload.receiptID = receiptId;
        const { error } = await dispatch(
          addResource<any, CheckInSmallParcelReceiptRequest>({
            baseUrl: `/receipts/${receiptId}/${checkInPath}`,
            payload,
            message: 'Successfully checked-in receipt',
            shouldSetUILoading: true,
          }),
        );

        if (!error) {
          setViewVersion ? setViewVersion((cur) => ++cur) : refresh();
          handleClose();
        }
      }
      setSubmitting(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [receiptId],
  );

  const formik = useFormik({
    initialValues: defaultReceiptCheckInSmallParcel,
    enableReinitialize: false,
    onSubmit: handleSubmit,
    validationSchema: schema(isDrayage),
  });

  const modalTitle = useMemo(
    () => (
      <span className={classes.title}>Update receipt shipment as arrived</span>
    ),
    [classes.title],
  );

  const formContent = useMemo(
    () => (
      <Grid container spacing={5} rowSpacing={2}>
        <Grid
          size={{
            xs: 12,
            sm: 12,
          }}
        >
          <TextInput
            name='palletsCount'
            formikProps={formik}
            label={
              isNonSmallParcel
                ? 'How many pallets were received?'
                : 'How many cartons were received?'
            }
            className={classes.input}
            type='number'
            required
          />
        </Grid>
        {isDrayage && (
          <Grid
            size={{
              xs: 12,
              sm: 12,
            }}
          >
            <TextInput
              name='containerNumber'
              formikProps={formik}
              label='Container number'
              className={classes.input}
              required
            />
          </Grid>
        )}
        <Grid
          size={{
            xs: 12,
            sm: 12,
          }}
        >
          <SelectInput
            name='managerUserID'
            formikProps={formik}
            label='Assign a manager'
            options={managers}
            className={classes.input}
            required
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 12,
          }}
        >
          <SwitchInput
            name='saveInNonSpecificLocation'
            label='Save in non-specific location'
            formikProps={formik}
          />
        </Grid>
        {!formik.values.saveInNonSpecificLocation && (
          <Grid
            size={{
              xs: 12,
              sm: 12,
            }}
          >
            <TextInput
              name='warehouseStagingLocationName'
              formikProps={formik}
              label='Select a location'
              className={classes.input}
              required
            />
          </Grid>
        )}
      </Grid>
    ),
    [classes.input, formik, isDrayage, isNonSmallParcel, managers],
  );

  return isMobile ? (
    <MobileModalForm
      btnText='SAVE'
      callback={formik.handleSubmit}
      handleCancel={handleClose}
      handleClose={handleClose}
      open={open}
      submitting={submitting}
      title={modalTitle}
      typeSubmit
    >
      {formContent}
    </MobileModalForm>
  ) : (
    <FormModal
      btnText='SAVE'
      callback={formik.handleSubmit}
      handleClose={handleClose}
      open={open}
      submitting={submitting}
      title={modalTitle}
      typeSubmit
    >
      {formContent}
    </FormModal>
  );
}

const useStyles = makeStyles()((theme) => ({
  title: {
    fontFamily: 'Montserrat',
    fontSize: 20,
    fontWeight: 600,
  },
  input: {
    width: '100%',
  },
}));

const schema = (isDrayage: boolean) =>
  object().shape({
  palletsCount: string().required('Required'),
  containerNumber: isDrayage ? string().required('Required') : string(),
  managerUserID: string().required('Required'),
  warehouseStagingLocationName: string()
    .nullable()
    .when('saveInNonSpecificLocation', {
      is: false,
      then: (schema) => schema.required('Required'),
    }),
  });
